import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, WifiOff, BellRing, CheckCircle2, RefreshCw, Building2, History, CalendarClock, BarChart3, Send, X, Plus, Layers } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "kiosk_manager", label: "Kiosk managers" },
  { value: "super_admin", label: "Super Admins" },
];
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const STAGE_LABELS = ["1st alert", "2nd alert", "3rd alert", "4th alert", "5th alert", "6th alert"];
const KIND_LABEL = { stage: "Offline alert", escalation: "Escalation", reminder: "Reminder", back_online: "Back online" };

function RecipientPicker({ testid, value, onChange, disabled }) {
  const toggle = (role) => {
    const set = new Set(value || []);
    set.has(role) ? set.delete(role) : set.add(role);
    onChange([...set]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {ROLE_OPTIONS.map((r) => {
        const on = (value || []).includes(r.value);
        return (
          <button key={r.value} type="button" disabled={disabled} data-testid={`${testid}-${r.value}`} onClick={() => toggle(r.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors disabled:opacity-50 ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-indigo-400"}`}>
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function DailySummarySchedule() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/daily-summary").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const patch = (u) => setCfg((c) => ({ ...c, ...u }));
  const toggleDay = (d) => setCfg((c) => {
    const set = new Set(c.days || []);
    set.has(d) ? set.delete(d) : set.add(d);
    return { ...c, days: [...set].sort((a, b) => a - b) };
  });

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/daily-summary", cfg);
      setCfg(r.data);
      toast.success("Daily summary schedule saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save schedule"); }
    finally { setSaving(false); }
  };

  if (!cfg) return null;
  const hh = String(cfg.hour ?? 8).padStart(2, "0");
  const mm = String(cfg.minute ?? 0).padStart(2, "0");

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-4" data-testid="daily-summary-schedule">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4 text-indigo-600" />
          <div>
            <p className="font-semibold text-slate-900 dark:text-white">Daily sales summary</p>
            <p className="text-xs text-slate-400 mt-0.5">When the per-kiosk sales recap (revenue, orders, top products) is sent. Recipients opt in under their profile.</p>
          </div>
        </div>
        <Switch data-testid="daily-summary-enabled" checked={cfg.enabled} onCheckedChange={(v) => patch({ enabled: v })} />
      </div>
      <div className={cfg.enabled ? "space-y-4" : "space-y-4 opacity-50 pointer-events-none"}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-slate-500 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Send at</span>
          <Input type="time" data-testid="daily-summary-time" value={`${hh}:${mm}`}
            onChange={(e) => { const [h, m] = e.target.value.split(":"); patch({ hour: parseInt(h || "0", 10) || 0, minute: parseInt(m || "0", 10) || 0 }); }}
            className="h-9 w-32" />
          <span className="text-xs text-slate-400">UK time (Europe/London)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {DAY_LABELS.map((lbl, idx) => {
            const on = (cfg.days || []).includes(idx);
            return (
              <button key={idx} type="button" data-testid={`daily-summary-day-${idx}`} onClick={() => toggleDay(idx)}
                className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-indigo-400"}`}>
                {lbl}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end">
        <Button data-testid="daily-summary-save" onClick={save} disabled={saving} size="sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save schedule</>}
        </Button>
      </div>
    </div>
  );
}

export default function OfflineAlerts() {
  const [buildings, setBuildings] = useState([]);
  const [scope, setScope] = useState("global");
  const [cfg, setCfg] = useState(null);
  const [override, setOverride] = useState(true);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/settings/offline-alerts/test");
      if (r.data.ok) toast.success(`Test alert email sent to ${r.data.sent_to}`);
      else toast.error(r.data.error || "Test email failed", { duration: 8000 });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send test"); }
    finally { setTesting(false); }
  };

  const loadScope = useCallback(async (sc) => {
    setLoading(true);
    try {
      if (sc === "global") {
        const r = await api.get("/settings/offline-alerts");
        setCfg(r.data); setOverride(true);
      } else {
        const r = await api.get(`/settings/offline-alerts/building/${sc}`);
        const { override: ov, ...rest } = r.data;
        setCfg(rest); setOverride(!!ov);
      }
    } catch { toast.error("Could not load alert settings"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
    api.get("/alert-log?limit=50").then((r) => setLog(r.data)).catch(() => {});
    loadScope("global");
  }, [loadScope]);

  const onScopeChange = (sc) => { setScope(sc); loadScope(sc); };
  const patch = (u) => setCfg((c) => ({ ...c, ...u }));
  const patchStage = (i, u) => setCfg((c) => ({ ...c, stages: c.stages.map((s, idx) => (idx === i ? { ...s, ...u } : s)) }));
  const min1 = (v) => Math.max(1, parseInt(v || "1", 10) || 1);
  const locked = scope !== "global" && !override;

  const save = async () => {
    setSaving(true);
    try {
      if (scope === "global") {
        const r = await api.put("/settings/offline-alerts", cfg);
        setCfg(r.data);
        toast.success("Global alert settings saved — applied to all kiosks");
      } else {
        const r = await api.put(`/settings/offline-alerts/building/${scope}`, { override, ...cfg });
        const { override: ov, ...rest } = r.data;
        setCfg(rest); setOverride(!!ov);
        toast.success(override ? "Custom timings saved for this building" : "Building reverted to global timings");
      }
      api.get("/alert-log?limit=50").then((res) => setLog(res.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save settings"); }
    finally { setSaving(false); }
  };

  if (loading || !cfg) {
    return <div className="p-10 text-slate-400" data-testid="offline-alerts-loading"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading…</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto w-full space-y-6 pt-6 md:pt-8" data-testid="offline-alerts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-admin-head text-3xl font-extrabold flex items-center gap-2"><BellRing className="h-7 w-7 text-indigo-600" /> Alert Management</h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">Control when offline alerts fire and who receives them. A kiosk shows offline after ~2 minutes without a heartbeat (live status is real-time). Alert emails are evaluated on a schedule — every minute in preview, but <b>every 15 minutes on the live site</b> (the platform's minimum for deployed apps), so in production the first offline email arrives at the next 15-minute check.</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="offline-alerts-test" onClick={sendTest} disabled={testing} variant="outline">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send me a test</>}
          </Button>
          <Button data-testid="offline-alerts-save" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save changes</>}
          </Button>
        </div>
      </div>

      <p className="text-xs text-slate-400 -mt-3" data-testid="offline-recipient-hint">Tip: an admin only receives an alert if their <b>role is selected on that stage</b> and — for offline emails — their <b>Profile → Kiosk offline alerts (email)</b> is on. Kiosk managers get alerts only for kiosks in <b>their assigned buildings</b>. Use “Send me a test” to confirm your own inbox works.</p>

      <DailySummarySchedule />

      {/* Scope selector */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2"><Building2 className="h-4 w-4 text-indigo-500" /> Applies to</span>
        <select data-testid="offline-scope-select" value={scope} onChange={(e) => onScopeChange(e.target.value)}
          className="h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm">
          <option value="global">All buildings (global default)</option>
          {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {scope !== "global" && (
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 ml-auto">
            <Switch data-testid="offline-building-override" checked={override} onCheckedChange={setOverride} />
            Use custom timings for this building
          </label>
        )}
      </div>

      {scope !== "global" && !override && (
        <p className="text-xs text-amber-600 -mt-3">This building follows the global default. Turn on “Use custom timings” to override it.</p>
      )}

      {/* Master toggle */}
      <div className={`rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex items-center justify-between ${locked ? "opacity-50" : ""}`}>
        <div>
          <p className="font-semibold text-slate-900 dark:text-white">Offline alerts</p>
          <p className="text-xs text-slate-400 mt-0.5">Master switch for kiosk-offline notifications{scope !== "global" ? " in this building" : ""}.</p>
        </div>
        <Switch data-testid="offline-alerts-enabled" disabled={locked} checked={cfg.enabled} onCheckedChange={(v) => patch({ enabled: v })} />
      </div>

      <div className={cfg.enabled && !locked ? "" : "opacity-50 pointer-events-none"}>
        {/* Stages */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 divide-y divide-slate-100 dark:divide-slate-800">
          <div className="px-4 py-3 flex items-center gap-2 text-slate-900 dark:text-white"><WifiOff className="h-4 w-4 text-rose-500" /> <span className="font-semibold">Alert stages</span></div>
          <p className="px-4 pt-1 pb-2 text-xs text-slate-400" data-testid="offline-stage-cadence-note">On the live site checks run every ~15 minutes, so any stages set under 15 minutes are all evaluated together at the first 15-minute check (they can't fire sooner in production). If no one is selected on the stage that fires — or no manager is assigned to the building — the alert automatically falls back to your Super Admins so it's never missed.</p>
          {cfg.stages.map((s, idx) => (
            <div key={idx} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`offline-stage-${idx}`}>
              <span className="text-xs font-bold text-slate-400 w-16 shrink-0">{STAGE_LABELS[idx] || `Alert ${idx + 1}`}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">After</span>
                <Input type="number" min={1} data-testid={`offline-stage-minutes-${idx}`} value={s.minutes}
                  onChange={(e) => patchStage(idx, { minutes: parseInt(e.target.value || "0", 10) || 0 })}
                  onBlur={(e) => patchStage(idx, { minutes: min1(e.target.value) })}
                  className="h-8 w-20 text-center" />
                <span className="text-sm text-slate-500">min offline</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-slate-400">Notify</span>
                <RecipientPicker testid={`offline-stage-recipients-${idx}`} value={s.recipients} onChange={(v) => patchStage(idx, { recipients: v })} />
              </div>
            </div>
          ))}
        </div>

        {/* Repeat */}
        <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><RefreshCw className="h-4 w-4 text-amber-500" /><div>
              <p className="font-semibold text-slate-900 dark:text-white">Keep reminding after the last alert</p>
              <p className="text-xs text-slate-400 mt-0.5">Re-send a reminder to the final stage's recipients until the kiosk is back online. Automatically stops once the kiosk moves into the merged offline report (below).</p>
            </div></div>
            <Switch data-testid="offline-repeat-enabled" checked={cfg.repeat_enabled} onCheckedChange={(v) => patch({ repeat_enabled: v })} />
          </div>
          {cfg.repeat_enabled && (
            <div className="flex items-center gap-2 pl-6">
              <span className="text-sm text-slate-500">Repeat every</span>
              <Input type="number" min={1} data-testid="offline-repeat-minutes" value={cfg.repeat_minutes}
                onChange={(e) => patch({ repeat_minutes: parseInt(e.target.value || "0", 10) || 0 })}
                onBlur={(e) => patch({ repeat_minutes: min1(e.target.value) })} className="h-8 w-24 text-center" />
              <span className="text-sm text-slate-500">minutes</span>
            </div>
          )}
        </div>

        {/* Back online */}
        <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500" /><div>
              <p className="font-semibold text-slate-900 dark:text-white">Back-online confirmation</p>
              <p className="text-xs text-slate-400 mt-0.5">Send a "back online" email once a recovered kiosk has stayed online continuously for the set time.</p>
            </div></div>
            <Switch data-testid="offline-backonline-enabled" checked={cfg.back_online_enabled} onCheckedChange={(v) => patch({ back_online_enabled: v })} />
          </div>
          {cfg.back_online_enabled && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Online for</span>
                <Input type="number" min={1} data-testid="offline-backonline-minutes" value={cfg.back_online_minutes}
                  onChange={(e) => patch({ back_online_minutes: parseInt(e.target.value || "0", 10) || 0 })}
                  onBlur={(e) => patch({ back_online_minutes: min1(e.target.value) })} className="h-8 w-20 text-center" />
                <span className="text-sm text-slate-500">min before confirming</span>
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-slate-400">Notify</span>
                <RecipientPicker testid="offline-backonline-recipients" value={cfg.back_online_recipients} onChange={(v) => patch({ back_online_recipients: v })} />
              </div>
            </div>
          )}
        </div>
        {/* Merged offline report */}
        <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 space-y-3" data-testid="merged-report-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2"><Layers className="h-4 w-4 text-indigo-500" /><div>
              <p className="font-semibold text-slate-900 dark:text-white">Merged offline report</p>
              <p className="text-xs text-slate-400 mt-0.5">Once a kiosk has been offline a while, stop the per-kiosk reminders and instead include it in a single digest sent on a fixed schedule. Super Admins get every site; Kiosk Managers get only their own buildings. Nothing is sent when no kiosk qualifies.</p>
            </div></div>
            <Switch data-testid="merged-report-enabled" checked={!!cfg.merged_report_enabled} onCheckedChange={(v) => patch({ merged_report_enabled: v })} />
          </div>
          {cfg.merged_report_enabled && (
            <div className="pl-6 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500">Move to the report after</span>
                <Input type="number" min={15} data-testid="merged-after-minutes" value={cfg.merged_after_minutes ?? 120}
                  onChange={(e) => patch({ merged_after_minutes: parseInt(e.target.value || "0", 10) || 0 })}
                  onBlur={(e) => patch({ merged_after_minutes: Math.max(15, parseInt(e.target.value || "15", 10) || 15) })}
                  className="h-8 w-24 text-center" />
                <span className="text-sm text-slate-500">min offline{(cfg.merged_after_minutes ?? 0) >= 60 ? ` (~${(cfg.merged_after_minutes / 60).toFixed(cfg.merged_after_minutes % 60 ? 1 : 0)}h)` : ""}</span>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-2"><CalendarClock className="h-4 w-4" /> Send the report at <span className="text-xs text-slate-400">(UK time)</span></div>
                <div className="flex flex-wrap gap-2">
                  {(cfg.merged_report_times || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-1" data-testid={`merged-time-${i}`}>
                      <Input type="time" value={t} data-testid={`merged-time-input-${i}`}
                        onChange={(e) => patch({ merged_report_times: (cfg.merged_report_times || []).map((x, idx) => (idx === i ? e.target.value : x)) })}
                        className="h-8 w-28" />
                      <button type="button" data-testid={`merged-time-remove-${i}`} onClick={() => patch({ merged_report_times: (cfg.merged_report_times || []).filter((_, idx) => idx !== i) })}
                        className="text-slate-400 hover:text-rose-500 p-1"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" data-testid="merged-time-add" className="h-8"
                    onClick={() => patch({ merged_report_times: [...(cfg.merged_report_times || []), "06:00"] })}>
                    <Plus className="h-4 w-4 mr-1" /> Add time
                  </Button>
                </div>
                {!(cfg.merged_report_times || []).length && <p className="text-xs text-amber-600 mt-1">Add at least one time or the report won't send.</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Alert log */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4" data-testid="offline-alert-log">
        <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2"><History className="h-4 w-4 text-slate-500" /> Recent alerts</h3>
        <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
          {log.length ? log.map((r) => (
            <div key={r.id} data-testid={`alert-log-${r.id}`} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${r.kind === "back_online" ? "bg-emerald-100 text-emerald-700" : r.kind === "escalation" || r.kind === "reminder" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
                  {KIND_LABEL[r.kind] || r.kind}{r.minutes ? ` · ${r.minutes}m` : ""}
                </span>
                <span className="font-medium text-slate-900 dark:text-white">{r.kiosk_name}</span>
                <span className="text-xs text-slate-400">{r.code}</span>
              </div>
              <span className="text-xs text-slate-400">{r.recipients} recipient{r.recipients === 1 ? "" : "s"} · {(r.at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
          )) : <p className="text-sm text-slate-400">No alerts have fired yet.</p>}
        </div>
      </div>
    </div>
  );
}
